/**
 * PaymentsFilterSheet — мобильный bottom-sheet фильтров журнала: чипы статуса
 * (живой выбор) + строка периода (стаб) + «Показать N». Применение сразу.
 */

import type {ReactNode} from 'react';

import type {PaymentStatus} from '@services/payments';
import {PeriodField, type Range} from '@components/PeriodPicker';
import {SheetGrabber} from '@components/SheetGrabber';
import {Sheet, SheetContent} from '@components/ui/Sheet';
import {cn} from '@lib/utils';
import {formatNumber} from '@utils/format';

import {STATUS_FILTER_OPTIONS} from '../../Payments.utils';

type PaymentsFilterSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: PaymentStatus | null;
  onStatus: (v: PaymentStatus | null) => void;
  period: Range;
  onPeriod: (p: Range) => void;
  shownCount: number;
};

export const PaymentsFilterSheet = ({
  open,
  onOpenChange,
  status,
  onStatus,
  period,
  onPeriod,
  shownCount,
}: PaymentsFilterSheetProps) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side="bottom" showClose={false} className="gap-0 rounded-t-[22px] p-5 pb-6">
      <SheetGrabber />
      <div className="mb-4 flex items-center justify-between">
        <div className="text-lg font-bold -tracking-[.01em]">Фильтры</div>
        <button
          onClick={() => onStatus(null)}
          disabled={status === null}
          className="text-[13.5px] font-medium text-primary transition-opacity disabled:pointer-events-none disabled:opacity-40"
        >
          Сбросить
        </button>
      </div>

      <div className="mb-2.5 text-[12.5px] font-semibold text-skr-ink2">Статус</div>
      <div className="mb-5 flex flex-wrap gap-2">
        <Chip active={status === null} onClick={() => onStatus(null)}>
          Все
        </Chip>
        {STATUS_FILTER_OPTIONS.map((o) => (
          <Chip key={o.value} active={status === o.value} onClick={() => onStatus(o.value)}>
            <span className="size-[7px] rounded-full" style={{background: o.dot}} />
            {o.label}
          </Chip>
        ))}
      </div>

      <PeriodField value={period} onChange={onPeriod} mobile variant="row" />

      <button
        onClick={() => onOpenChange(false)}
        className="mt-4 h-[50px] w-full rounded-[13px] bg-primary text-[15.5px] font-semibold text-primary-foreground shadow-[0_4px_10px_rgba(44,92,219,.28)]"
      >
        Показать {formatNumber(shownCount)}
      </button>
    </SheetContent>
  </Sheet>
);

const Chip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    onClick={onClick}
    className={cn(
      'inline-flex h-[34px] items-center gap-1.5 rounded-[10px] border px-[13px] text-[13px]',
      active
        ? 'border-skr-info-line bg-accent font-semibold text-primary'
        : 'border-skr-line bg-card text-skr-ink2',
    )}
  >
    {children}
  </button>
);
